// Pure view-model for the v3 executive deck: the hero number, then technical win -> what changes ->
// business value, one slide per use case with the customers who proved it underneath. Leaf module: no React, no assets, and — by the
// calculator -> deck boundary in CLAUDE.md — nothing from the engine. It reads only the math-free
// ValueStory snapshot, engine/format and the authored copy in data/use-case-story-copy, so the slides
// are dumb renderers of this shape and the numbers can be unit-tested without a DOM.

import { useCaseStoryCopy } from '../../../data/use-case-story-copy';
import { money, num, returnMultipleText } from '../../../engine/format';
import type { StoryMetric, StoryProof, StoryUseCase, ValueStory } from '../../../engine/value-story-schema';

/* ── copy that the tests and the slides share ── */

export const FORMULA_LABEL = 'Total Program Value × Attribution Factor (varies by use case) = Clay-Attributed Value';
export const ROI_ABSENT_COPY = 'Add a contract estimate to see ROI';
/**
 * The estimate caveat, carried over from v2's ROI slide. v2 pointed at its "Inputs & assumptions"
 * slides, which the v3 deck does not have, so the pointer now names the calculator instead.
 */
export const ESTIMATE_DISCLAIMER =
  'Value estimate — every figure is modeled from the inputs captured in the calculator and is not a guarantee of results.';

/** Customer story cards on a use case slide, at most. */
export const STORIES_PER_SLIDE = 3;

/* ── formatting ── */

/** `40` + `% of account records` -> `40%`; `3` + `hrs / rep` -> `3 hrs / rep`. */
export function metricValueText(value: number, unit: string): string {
  const n = num(value);
  const u = unit.trim();
  return u.startsWith('%') ? `${n}%` : u ? `${n} ${u}` : n;
}

/** The descriptive tail once a leading `%` is shown in the number: `% of account records` -> `of account records`. */
function metricScope(unit: string): string {
  const u = unit.trim();
  return u.startsWith('%') ? u.slice(1).trim() : '';
}

export interface MetricLine {
  key: string;
  name: string;
  scope: string;
  todayText: string;
  /** null when the metric has no today / with-Clay split (e.g. accounts enrolled). */
  withClayText: string | null;
  measurement: StoryMetric['measurement'];
  /** true when today / with-Clay came from the question defaults rather than the customer. */
  assumed: boolean;
}

function metricLine(m: StoryMetric): MetricLine {
  return {
    key: m.key,
    name: m.name,
    scope: metricScope(m.unit),
    todayText: metricValueText(m.today, m.unit),
    withClayText: m.withClay == null ? null : metricValueText(m.withClay, m.unit),
    measurement: m.measurement,
    assumed: m.assumed,
  };
}

/* ── slide 1: the number ── */

export interface NumberSlideVm {
  customerName: string;
  grossText: string;
  programText: string;
  attributedText: string;
  /** Attribution across the sized use cases, e.g. `25%`, or `15–35%` when it varies. */
  attributionText: string | null;
  formulaLabel: string;
  /** null when no contract estimate was entered. */
  roiText: string | null;
  roiAbsentCopy: string;
  contractText: string | null;
  /** The secondary pipeline line; null when no use case creates pipeline. */
  pipelineText: string | null;
  useCaseCount: number;
}

function attributionRange(ucs: StoryUseCase[]): string | null {
  if (ucs.length === 0) return null;
  const pcts = ucs.map((u) => u.attributionPct);
  const lo = Math.min(...pcts);
  const hi = Math.max(...pcts);
  return lo === hi ? `${num(lo)}%` : `${num(lo)}–${num(hi)}%`;
}

function numberSlide(story: ValueStory): NumberSlideVm {
  const { rollup, deal } = story;
  const estimate = deal.contractEstimate;
  const hasEstimate = estimate != null && estimate > 0;
  return {
    customerName: story.customerName,
    grossText: money(rollup.gross),
    programText: money(rollup.program),
    attributedText: money(rollup.attributed),
    attributionText: attributionRange(story.useCases),
    formulaLabel: FORMULA_LABEL,
    roiText: hasEstimate ? returnMultipleText(rollup.attributed, estimate) : null,
    roiAbsentCopy: ROI_ABSENT_COPY,
    contractText: hasEstimate ? money(estimate) : null,
    pipelineText: rollup.pipeline > 0 ? `${money(rollup.pipeline)} pipeline created` : null,
    useCaseCount: story.useCases.length,
  };
}

/* ── slides 2+: technical win → what changes → business value ── */

export type RowTone = 'slushie' | 'blueberry' | 'lime';
const ROW_TONES: RowTone[] = ['slushie', 'blueberry', 'lime'];

/** Shown in the technical-win cell when an old snapshot carries no leading metric for the use case. */
export const NO_METRIC_TEXT = 'Leading metric not captured';

export interface UseCaseRowVm {
  id: string;
  name: string;
  /** The headline leading metric from the snapshot, whole: "CRM fill rate: 40% → 82%". */
  winText: string;
  /** The same, split for the slide: the metric name, and its today → with Clay values. */
  winName: string;
  winValue: string;
  /** Authored, the same for every customer; empty for a use case with no authored copy (retired ids). */
  changeText: string;
  /** The authored business-value phrase; empty when there is no authored copy. */
  valuePhrase: string;
  /** This use case's program value, per year: "$1.2M/yr". */
  valueAmountText: string;
  tone: RowTone;
}

/** A customer who proved it: name, the one result worth quoting, and the one-line context. */
export interface StoryCardVm {
  customer: string;
  /** Improvement, else `pre → post`, else whichever of post / pre is set; empty when the row has no result. */
  metric: string;
  note: string;
}

export interface UseCaseSlideVm {
  row: UseCaseRowVm;
  stories: StoryCardVm[];
  /** 1-based position among the sized use cases, for "Use case i of n". */
  index: number;
  count: number;
}

/** The result a story card leads with. */
export function storyMetricText(s: StoryProof): string {
  const imp = s.imp.trim();
  if (imp) return imp;
  const pre = s.pre.trim();
  const post = s.post.trim();
  if (pre && post) return `${pre} → ${post}`;
  return post || pre;
}

function storyCard(s: StoryProof): StoryCardVm {
  return { customer: s.customer, metric: storyMetricText(s), note: s.notes.trim() };
}

const GTM_HOURS_METRIC_KEY = 'uc-gtm-automation-hours';

/** The headline metric, `today → with Clay` (or today alone when there is no split: "Tools displaced: 1"). */
function winParts(u: StoryUseCase): { name: string; value: string } {
  const m = u.metrics[0] ? metricLine(u.metrics[0]) : null;
  if (!m) return { name: '', value: NO_METRIC_TEXT };
  // No split (tools displaced): the bare number; the metric name already says what is counted.
  if (m.withClayText === null) return { name: m.name, value: num(u.metrics[0].today) };
  // GTM automation's hours start at zero by definition, so "0 → 156" says nothing: just the saving.
  if (u.metrics[0].key === GTM_HOURS_METRIC_KEY) {
    return { name: m.name, value: `${num(u.metrics[0].withClay ?? 0)} hours saved` };
  }
  // A unit word is said once, after both numbers ("300 → 900 contacts / month"), so the slab stays short.
  const unit = u.metrics[0].unit.trim();
  if (unit && !unit.startsWith('%')) {
    return { name: m.name, value: `${num(u.metrics[0].today)} → ${num(u.metrics[0].withClay ?? 0)} ${unit}` };
  }
  return { name: m.name, value: `${m.todayText} → ${m.withClayText}` };
}

function useCaseRow(u: StoryUseCase, index: number): UseCaseRowVm {
  const copy = useCaseStoryCopy(u.id);
  const win = winParts(u);
  return {
    id: u.id,
    name: u.name,
    winText: win.name ? `${win.name}: ${win.value}` : win.value,
    winName: win.name,
    winValue: win.value,
    changeText: copy?.change ?? '',
    valuePhrase: copy?.value ?? '',
    valueAmountText: `${money(u.programValue)}/yr`,
    tone: ROW_TONES[index % ROW_TONES.length],
  };
}

/** One slide per selected use case, in the snapshot's (calculator) order. */
function buildUseCaseSlides(story: ValueStory): UseCaseSlideVm[] {
  const count = story.useCases.length;
  return story.useCases.map((u, i) => ({
    row: useCaseRow(u, i),
    stories: u.stories.slice(0, STORIES_PER_SLIDE).map(storyCard),
    index: i + 1,
    count,
  }));
}

/* ── the deck ── */

export interface DeckViewModel {
  number: NumberSlideVm;
  /** One technical win → what changes → business value slide per use case, after the hero. */
  useCaseSlides: UseCaseSlideVm[];
  dateText: string;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** `created` is epoch ms from the snapshot; 0 / missing gives an empty string rather than 1970. */
export function dateText(created: number): string {
  if (!created) return '';
  const d = new Date(created);
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function buildDeckViewModel(story: ValueStory): DeckViewModel {
  return {
    number: numberSlide(story),
    useCaseSlides: buildUseCaseSlides(story),
    dateText: dateText(story.created),
  };
}
